/**
 * Instance Configuration System
 *
 * Drives variant-specific behavior (procurement vs funding) based on the
 * NEXT_PUBLIC_NOLIA_VARIANT environment variable set at deploy time.
 *
 * Each Terraform environment sets different env vars → same Docker images,
 * different behavior. That promise is only true because the values are resolved at
 * RUNTIME via `runtime-config.ts` — see that file for why a literal env read breaks it.
 */
import { readRuntimeConfig, type NoliaVariant } from './runtime-config';

export type { NoliaVariant };

export interface KBCategoryConfig {
  id: string;
  labelKey: string;
  url: string;
  /** Filter tabs shown on the KB list page (e.g., ['Funds', 'Grants', 'Scholarships']) */
  filterTabs?: string[];
  /** Wizard config ID used when creating a KB of this category */
  wizardConfigId: string;
}

export interface NavigationItem {
  labelKey: string;
  href: string;
  iconName: string;
  adminOnly?: boolean;
  disabled?: boolean;
  footer?: boolean;
}

export interface InstanceConfig {
  variant: NoliaVariant;

  /**
   * Client identifier baked into the frontend at build time (NEXT_PUBLIC_CLIENT_NAME).
   * Threaded through funding-pipeline calls as `clientName` → `metadata.client_name`
   * on Numa calls. Forward-compat for a shared multi-tenant backend; today the
   * backend has its own CLIENT_NAME env fallback. Defaults to 'ngaitahu'.
   */
  clientName: string;

  /** KB category definitions for this variant */
  kbCategories: KBCategoryConfig[];

  /** Sidebar navigation items */
  navigation: {
    adminItems: NavigationItem[];
    operatorItems: NavigationItem[];
    footerItems: NavigationItem[];
  };

  /** Feature toggles */
  features: {
    documentValidation: boolean;
    languageSelection: boolean;
    projectKB: boolean;
    applicationSetup: boolean;
  };

  /** The KB category ID used for "activity" assignment in user management */
  activityKBCategory: string;

  /** i18n key prefix for variant-specific terminology */
  terminologyPrefix: string;

  /** Assessment routes */
  assessmentBasePath: string;
}

/**
 * Resolve the variant.
 *
 * Reads through the runtime bridge (`runtime-config.ts`) rather than a literal
 * `process.env.NEXT_PUBLIC_NOLIA_VARIANT`. A literal is inlined by Next.js at BUILD time,
 * which pins one image to one deployment and makes the dev -> stg -> demo ladder
 * impossible. The bridge resolves from the live container env on the server and from the
 * server-injected window global on the client.
 *
 * Behaviour is unchanged for a baked image: `frontend/Dockerfile` sets `ENV NEXT_PUBLIC_*`,
 * so the baked values are in the container env and resolve identically.
 *
 * Default stays 'procurement' for FE/BE parity (backend `getInstanceConfig()` does the same,
 * and CLAUDE.md §10 documents it). Flipping that default must be done in lockstep across FE,
 * BE and the docs. The bridge warns on both unset and unknown.
 */
export function resolveVariant(): NoliaVariant {
  return readRuntimeConfig().variant;
}

/** Client identifier. Runtime-resolved for the same reason as `resolveVariant()`. */
export function resolveClientName(): string {
  return readRuntimeConfig().clientName;
}
